export const DEVICE_KINDS = ['microphone', 'camera', 'screen'] as const

export type DeviceKind = (typeof DEVICE_KINDS)[number]

export type DevicePermissionStatus =
  'unknown' | 'prompt' | 'granted' | 'denied' | 'unavailable' | 'checking' | 'error'

export type DevicePermissionSnapshot = {
  status: DevicePermissionStatus
  detail?: string
}

export type PermissionQueryState = 'granted' | 'denied' | 'prompt' | 'unsupported'

export type DeviceMediaBridge = {
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>
  getDisplayMedia?: (constraints?: DisplayMediaStreamOptions) => Promise<MediaStream>
}

export const DEVICE_PERMISSION_QUERY_NAME: Record<DeviceKind, string> = {
  microphone: 'microphone',
  camera: 'camera',
  screen: 'display-capture',
}

export const DEVICE_LABELS: Record<DeviceKind, string> = {
  microphone: 'Microphone',
  camera: 'Camera',
  screen: 'Screen share',
}

/** UX copy only — checking a device is never blocked by these labels. */
export const DEVICE_REQUIREMENT_LABELS: Record<DeviceKind, 'Required' | 'Optional'> = {
  microphone: 'Required',
  camera: 'Optional',
  screen: 'Optional',
}

export const DENIED_DETAIL =
  'Blocked in this browser. Allow access in site settings for this site, then try again.'

export const emptyDevicePermissions = (): Record<DeviceKind, DevicePermissionSnapshot> => ({
  camera: { status: 'unknown' },
  microphone: { status: 'unknown' },
  screen: { status: 'unknown' },
})

export const deviceStatusLabel = (status: DevicePermissionStatus): string => {
  switch (status) {
    case 'granted':
      return 'Ready'
    case 'denied':
      return 'Blocked'
    case 'unavailable':
      return 'No device'
    case 'checking':
      return 'Checking'
    case 'error':
      return 'Couldn’t check'
    case 'prompt':
    case 'unknown':
    default:
      return 'Not checked'
  }
}

export const deviceStatusPalette = (status: DevicePermissionStatus): string => {
  switch (status) {
    case 'granted':
      return 'green'
    case 'denied':
    case 'error':
      return 'red'
    case 'unavailable':
      return 'yellow'
    default:
      return 'gray'
  }
}

export const deviceCheckButtonLabel = (status: DevicePermissionStatus): string => {
  if (status === 'granted') {
    return 'Recheck'
  }
  if (status === 'denied' || status === 'error' || status === 'unavailable') {
    return 'Try again'
  }
  return 'Check'
}

export const allDevicesReady = (devices: Record<DeviceKind, DevicePermissionSnapshot>): boolean =>
  DEVICE_KINDS.every(kind => devices[kind].status === 'granted')

export const snapshotFromQueryState = (state: PermissionQueryState): DevicePermissionSnapshot => {
  if (state === 'granted') {
    return { status: 'granted' }
  }
  if (state === 'denied') {
    return { status: 'denied', detail: DENIED_DETAIL }
  }
  if (state === 'prompt') {
    return { status: 'prompt' }
  }
  return { status: 'unknown' }
}

const errorName = (error: unknown): string => {
  if (error instanceof Error) {
    return error.name
  }
  return ''
}

export const snapshotFromRequestError = (
  error: unknown,
  kind: DeviceKind,
  queried?: PermissionQueryState
): DevicePermissionSnapshot => {
  const name = errorName(error)

  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return { status: 'unavailable', detail: 'No matching device found.' }
  }
  if (name === 'NotReadableError') {
    return { status: 'error', detail: 'This device is in use by another app.' }
  }
  if (name === 'NotSupportedError' || name === 'TypeError') {
    return {
      status: 'unavailable',
      detail: kind === 'screen' ? 'This browser cannot share your screen.' : 'Not supported in this browser.',
    }
  }
  if (name === 'SecurityError') {
    return { status: 'error', detail: 'This page is not allowed to use devices.' }
  }
  if (name === 'AbortError') {
    return { status: 'unknown', detail: 'Check was cancelled.' }
  }
  if (name === 'NotAllowedError') {
    if (queried === 'denied') {
      return { status: 'denied', detail: DENIED_DETAIL }
    }
    if (kind === 'screen' || queried === 'prompt' || queried === 'unsupported') {
      return {
        status: 'unknown',
        detail: kind === 'screen' ? 'Share picker was dismissed.' : 'Permission wasn’t granted.',
      }
    }
    return { status: 'denied', detail: DENIED_DETAIL }
  }

  const message = error instanceof Error && error.message ? error.message : 'Could not access this device.'
  return { status: 'error', detail: message }
}

export const stopMediaStream = (stream: MediaStream | { getTracks: () => Array<{ stop: () => void }> }) => {
  stream.getTracks().forEach(track => {
    track.stop()
  })
}

const namedError = (message: string, name: string): Error => {
  const error = new Error(message)
  error.name = name
  return error
}

export const queryDevicePermission = async (
  kind: DeviceKind,
  permissions?: Pick<Permissions, 'query'> | null
): Promise<PermissionQueryState> => {
  if (!permissions?.query) {
    return 'unsupported'
  }
  try {
    const result = await permissions.query({
      name: DEVICE_PERMISSION_QUERY_NAME[kind],
    } as PermissionDescriptor)
    if (result.state === 'granted' || result.state === 'denied' || result.state === 'prompt') {
      return result.state
    }
    return 'unsupported'
  } catch {
    return 'unsupported'
  }
}

export const requestDeviceMedia = async (
  kind: DeviceKind,
  mediaDevices: DeviceMediaBridge
): Promise<void> => {
  let stream: MediaStream
  if (kind === 'screen') {
    if (typeof mediaDevices.getDisplayMedia !== 'function') {
      throw namedError('Screen share is not available in this browser', 'NotSupportedError')
    }
    stream = await mediaDevices.getDisplayMedia({ video: true, audio: false })
  } else if (typeof mediaDevices.getUserMedia !== 'function') {
    throw namedError('Device access is not available in this browser', 'NotSupportedError')
  } else {
    stream = await mediaDevices.getUserMedia(
      kind === 'camera' ? { video: true, audio: false } : { audio: true, video: false }
    )
  }
  stopMediaStream(stream)
}

export const screenShareSupported = (mediaDevices?: DeviceMediaBridge | null): boolean =>
  typeof mediaDevices?.getDisplayMedia === 'function'
